import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CheckCircle, XCircle, AlertCircle, Loader2, Copy, RefreshCw } from "lucide-react";

const API_URL = "/api";
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

function CheckRow({ label, status, message, testid }) {
  const icon = {
    ok: <CheckCircle className="w-5 h-5 text-green-600" />,
    error: <XCircle className="w-5 h-5 text-red-600" />,
    warn: <AlertCircle className="w-5 h-5 text-amber-600" />,
    loading: <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
  }[status];

  return (
    <div className="flex items-start gap-3 p-3 border-b border-slate-200 dark:border-slate-700 last:border-0" data-testid={testid}>
      <div className="shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 dark:text-slate-100">{label}</div>
        <div className="text-sm text-slate-600 dark:text-slate-400 break-words font-mono">{message}</div>
      </div>
    </div>
  );
}

export default function Diagnose() {
  const [checks, setChecks] = useState({});
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);

  const setCheck = useCallback((key, status, message) => {
    setChecks((prev) => ({ ...prev, [key]: { status, message } }));
  }, []);

  const runDiagnostics = useCallback(async () => {
    setRunning(true);
    setChecks({});

    // 1. Browser info
    setCheck("browser", "ok", `${navigator.userAgent}`);

    // 2. Screen
    setCheck("screen", "ok", `${window.innerWidth}x${window.innerHeight} (Geräte: ${window.screen.width}x${window.screen.height})`);

    // 3. Time/Locale
    const now = new Date();
    setCheck("time", "ok", `${now.toISOString()} | Lokal: ${now.toLocaleString("de-DE")} | TZ: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

    // 4. App version
    setCheck("version", "ok", `Frontend: ${APP_VERSION} | URL: ${window.location.origin}`);

    // 5. localStorage
    try {
      localStorage.setItem("__diag_test", "1");
      localStorage.removeItem("__diag_test");
      setCheck("localStorage", "ok", "localStorage verfügbar");
    } catch (e) {
      setCheck("localStorage", "error", `localStorage nicht verfügbar: ${e.message}`);
    }

    // 6. Cookies
    setCheck("cookies", navigator.cookieEnabled ? "ok" : "error", navigator.cookieEnabled ? "Cookies aktiviert" : "Cookies BLOCKIERT - könnte Anmeldung verhindern");

    // 7. Online status
    setCheck("online", navigator.onLine ? "ok" : "error", navigator.onLine ? "Online" : "OFFLINE");

    // 8. Backend health check
    setCheck("backend_health", "loading", "Prüfe...");
    try {
      const r = await axios.get(`${API_URL}/health`, { timeout: 8000 });
      setCheck("backend_health", "ok", `Backend erreichbar: ${JSON.stringify(r.data)}`);
    } catch (e) {
      setCheck("backend_health", "error", `Backend nicht erreichbar: ${e.message} (${e.response?.status || "kein Status"})`);
    }

    // 9. Auth check (cookie-based session)
    setCheck("auth", "loading", "Prüfe...");
    try {
      const r = await axios.get(`${API_URL}/auth/me`, { timeout: 8000 });
      setCheck("auth", "ok", `Eingeloggt als: ${r.data.email} (Rolle: ${r.data.role || "user"})`);
    } catch (e) {
      if (e.response?.status === 401) {
        setCheck("auth", "warn", "Keine aktive Sitzung - bitte einloggen");
      } else {
        setCheck("auth", "error", `Auth fehlgeschlagen: ${e.response?.status} ${e.response?.data?.detail || e.message}`);
      }
    }

    // 10. CSS/JS Test - verify dark mode preference
    const darkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
    setCheck("darkmode", "ok", `System: ${darkMode ? "Dunkel" : "Hell"} | HTML class: ${document.documentElement.className || "(keine)"}`);

    // 11. Service Worker
    if ("serviceWorker" in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        setCheck("sw", regs.length > 0 ? "warn" : "ok", regs.length > 0 ? `${regs.length} Service Worker aktiv (kann Cache-Probleme verursachen)` : "Keine Service Worker (gut)");
      } catch (e) {
        setCheck("sw", "warn", `SW-Check fehlgeschlagen: ${e.message}`);
      }
    } else {
      setCheck("sw", "ok", "Service Worker nicht verfügbar");
    }

    setRunning(false);
  }, [setCheck]);

  useEffect(() => {
    runDiagnostics();
  }, [runDiagnostics]);

  const copyReport = () => {
    const report = Object.entries(checks)
      .map(([key, { status, message }]) => `[${status.toUpperCase()}] ${key}: ${message}`)
      .join("\n");
    const full = `=== R-Network CRM Diagnose ===\nZeitpunkt: ${new Date().toISOString()}\n\n${report}`;
    navigator.clipboard.writeText(full).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const checkOrder = [
    ["browser", "Browser"],
    ["screen", "Bildschirm"],
    ["time", "Datum & Uhrzeit"],
    ["version", "App-Version & URL"],
    ["online", "Internet-Verbindung"],
    ["cookies", "Cookies"],
    ["localStorage", "LocalStorage / Token"],
    ["backend_health", "Backend-Verbindung"],
    ["auth", "Authentifizierung"],
    ["darkmode", "Dark Mode"],
    ["sw", "Service Worker"]
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 sm:p-8" data-testid="diagnose-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-sky-600 to-cyan-500 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold">System-Diagnose</h1>
            <p className="text-sky-100 mt-1 text-sm">Automatische Prüfung deiner Umgebung. Schicke diesen Bericht an den Administrator bei Problemen.</p>
          </div>

          <div className="p-4 flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={runDiagnostics}
              disabled={running}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white rounded-md font-medium transition-colors"
              data-testid="diagnose-rerun-btn"
            >
              <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} />
              {running ? "Läuft..." : "Erneut prüfen"}
            </button>
            <button
              type="button"
              onClick={copyReport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 rounded-md font-medium transition-colors"
              data-testid="diagnose-copy-btn"
            >
              <Copy className="w-4 h-4" />
              {copied ? "Kopiert!" : "Bericht kopieren"}
            </button>
            <a
              href="/login"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 rounded-md font-medium transition-colors"
            >
              Zur Anmeldung
            </a>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {checkOrder.map(([key, label]) => {
              const check = checks[key] || { status: "loading", message: "Wartet..." };
              return <CheckRow key={key} label={label} status={check.status} message={check.message} testid={`diag-${key}`} />;
            })}
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-6">
          Diese Seite ist auch ohne Anmeldung erreichbar unter <code className="bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded">/diagnose</code>
        </p>
      </div>
    </div>
  );
}
